import { Type } from 'class-transformer';
import {
	ArrayMaxSize,
	IsArray,
	IsDateString,
	IsEmail,
	IsIn,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Max,
	MaxLength,
	Min,
	ValidateNested,
} from 'class-validator';
import {
	CAMPAIGN_CANT_EMAIL_RULES,
	CAMPAIGN_REP_RULES,
	type CampaignBuilderStep,
	type CampaignCantEmailRule,
	type CampaignLeftOutReason,
	type CampaignRepRule,
} from '../../constants/campaign-builder';
import type { CampaignStepType } from '../../constants/campaign-sequence';
import type { CampaignContentEntity } from '../entity/campaign-content.entity';
import type { CampaignContentVersionEntity } from '../entity/campaign-content-version.entity';
import type { CampaignStepEntity } from '../entity/campaign-step.entity';
import type { CampaignEntity } from '../entity/campaign.entity';
import type { InstantlyPeopleSlotsDto, InstantlySenderGroupDto } from './instantly.dto';

/** PD-17615–17619 — the campaign builder over the API. */

/** Most people one People-step add handles (the add runs inside the request). */
export const MAX_CAMPAIGN_PEOPLE_PER_ADD = 5000;

/** Most addresses a file source may list. */
export const MAX_CAMPAIGN_FILE_EMAILS = 5000;

// ---------------------------------------------------------------------------
// People rules (campaign.settings.peopleRules)
// ---------------------------------------------------------------------------

export class CampaignPeopleRulesDto {
	/** Most people per company; null = everyone. Leaders (owner, president, buyer...) are picked first. */
	@IsOptional()
	@IsInt()
	@Min(1)
	@Max(50)
	public perCompany!: number | null;

	@IsIn(CAMPAIGN_REP_RULES as unknown as Array<string>)
	public repRule!: CampaignRepRule;

	/** The rep for people whose account has none (account_rep), or everyone's rep (one_person). */
	@IsOptional()
	@IsInt()
	@Min(1)
	public repUserId!: number | null;

	/** A person whose email bounced or isn't verified: left out, or kept for calls and LinkedIn. */
	@IsIn(CAMPAIGN_CANT_EMAIL_RULES as unknown as Array<string>)
	public cantEmailRule!: CampaignCantEmailRule;
}

export const DEFAULT_CAMPAIGN_PEOPLE_RULES: Readonly<CampaignPeopleRulesDto> = {
	perCompany: 2,
	repRule: 'account_rep',
	repUserId: null,
	cantEmailRule: 'leave_out',
};

// ---------------------------------------------------------------------------
// People sources
// ---------------------------------------------------------------------------

export const CAMPAIGN_PEOPLE_SOURCE_KINDS = ['contacts_search', 'clients_search', 'map', 'competitor', 'file', 'people', 'trade_show', 'association'] as const;
export type CampaignPeopleSourceKind = (typeof CAMPAIGN_PEOPLE_SOURCE_KINDS)[number];

/** The lists of a trade show a campaign can draw on. */
export const CAMPAIGN_SHOW_LISTS = ['attendee', 'exhibitor', 'met_at_show'] as const;
export type CampaignShowList = (typeof CAMPAIGN_SHOW_LISTS)[number];

/**
 * Where people come from. One shape for every source so preview and add take
 * the same body; each kind reads only its own fields.
 *
 * - `contacts_search`: a saved search on the Contacts list (`savedSearchId`).
 * - `clients_search`: a saved search on the Clients list; everyone at those accounts.
 * - `map`: accounts within `radiusMiles` of `lat`/`lng` (Map and radius).
 * - `competitor`: accounts on record as using a competitor (`wholesalerId`).
 * - `file`: people matched by email address from an uploaded file (`emails`).
 * - `people`: chosen people (`contactIds`), e.g. from Apollo's Add new people.
 * - `trade_show`: one list of a trade show (`tradeShowId`, `showList`).
 * - `association`: an association's current members (`associationId`).
 */
export class CampaignPeopleSourceDto {
	@IsIn(CAMPAIGN_PEOPLE_SOURCE_KINDS as unknown as Array<string>)
	public kind!: CampaignPeopleSourceKind;

	@IsOptional()
	@IsInt()
	@Min(1)
	public savedSearchId?: number;

	@IsOptional()
	@IsNumber()
	@Min(-90)
	@Max(90)
	public lat?: number;

	@IsOptional()
	@IsNumber()
	@Min(-180)
	@Max(180)
	public lng?: number;

	@IsOptional()
	@IsNumber()
	@Min(1)
	@Max(500)
	public radiusMiles?: number;

	/** The place the radius is around, for the summary ("Anaheim Convention Center"). */
	@IsOptional()
	@IsString()
	@MaxLength(255)
	public placeLabel?: string;

	@IsOptional()
	@IsInt()
	@Min(1)
	public wholesalerId?: number;

	@IsOptional()
	@IsInt()
	@Min(1)
	public tradeShowId?: number;

	@IsOptional()
	@IsIn(CAMPAIGN_SHOW_LISTS as unknown as Array<string>)
	public showList?: CampaignShowList;

	/** The directory's id for the association. */
	@IsOptional()
	@IsInt()
	@Min(1)
	public associationId?: number;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(MAX_CAMPAIGN_FILE_EMAILS)
	@IsEmail({}, { each: true })
	public emails?: Array<string>;

	@IsOptional()
	@IsArray()
	@ArrayMaxSize(MAX_CAMPAIGN_PEOPLE_PER_ADD)
	@IsInt({ each: true })
	public contactIds?: Array<number>;
}

export class CampaignPeopleRequestDto {
	@ValidateNested()
	@Type(() => CampaignPeopleSourceDto)
	public source!: CampaignPeopleSourceDto;
}

export interface CampaignLeftOutCountDto {
	reason: CampaignLeftOutReason;
	count: number;
}

/** What the email check would do with the people an add would take (nothing is checked or spent by a preview). */
export interface CampaignEmailCheckSummaryDto {
	/** Good and checked within the window. */
	verifiedRecently: number;
	/** Would be checked before they are added. */
	needsCheck: number;
	/** MillionVerifier's price per check, when connected. */
	costPerCheck: number | null;
	connected: boolean;
	windowDays: number;
}

export interface CampaignMapPointDto {
	clientId: number;
	name: string;
	lat: number;
	lng: number;
	/** The account's stage meaning (customer, lead ...). */
	meaning: string | null;
}

/** A source before it is added: what Workd found and what it would do. */
export interface CampaignPeoplePreviewDto {
	/** People the source resolved to. */
	found: number;
	/** People who would be added (some for calls only). */
	toAdd: number;
	/** Of those, people kept for calls and LinkedIn only (no usable email). */
	callsOnly: number;
	companies: number;
	alreadyInCampaign: number;
	leftOut: Array<CampaignLeftOutCountDto>;
	emailCheck: CampaignEmailCheckSummaryDto;
	/** Accounts in the source with no coordinates (map source only). */
	companiesWithoutLocation: number | null;
	/** Map source: the accounts inside the radius, for the pins. */
	mapPoints: Array<CampaignMapPointDto>;
	/** File source: addresses no person in Workd has. */
	notInWorkd: Array<string>;
	/** True when the source matched more people than one add takes. */
	truncated: boolean;
}

export interface CampaignPeopleAddResultDto extends CampaignPeoplePreviewDto {
	added: number;
	/** Addresses MillionVerifier checked during the add. */
	checked: number;
	/** Why some addresses went unchecked, when some did. */
	emailCheckBlocker: string | null;
}

/** Who is in the campaign, as the People step's panel shows it. */
export interface CampaignPeopleSummaryDto {
	members: number;
	companies: number;
	callsOnly: number;
	withoutPhone: number;
	withoutLinkedIn: number;
	leftOut: Array<CampaignLeftOutCountDto>;
}

export interface CampaignPersonRowDto {
	/** Membership id for members; null for a person left out. */
	memberId: number | null;
	contactId: number;
	name: string | null;
	title: string | null;
	email: string | null;
	clientId: number | null;
	accountName: string | null;
	/** Left out: why. */
	reason: CampaignLeftOutReason | null;
	repName: string | null;
	callsOnly: boolean;
}

export interface CampaignPeoplePageDto {
	rows: Array<CampaignPersonRowDto>;
	total: number;
}

/** Saved searches and competitors the People step offers as sources. */
export interface CampaignPeopleSourceOptionsDto {
	contactSearches: Array<{ id: number; name: string }>;
	clientSearches: Array<{ id: number; name: string }>;
	competitors: Array<{ id: number; name: string; accounts: number }>;
	reps: Array<{ id: number; name: string }>;
	/** Shows with someone on a list. Empty without the trade shows capability. */
	tradeShows: Array<{ id: number; name: string; lists: Record<CampaignShowList, number> }>;
	/** Associations on the tenant's list with current members. Empty without the capability. */
	associations: Array<{ id: number; name: string; members: number }>;
}

// ---------------------------------------------------------------------------
// Copy: drafts, versions, a rep's own script, preview
// ---------------------------------------------------------------------------

/** What "Write drafts" / "Regenerate" writes. */
export const CAMPAIGN_DRAFT_GROUPS = ['emails', 'call_script', 'voicemail_gatekeeper', 'turnarounds', 'linkedin'] as const;
export type CampaignDraftGroup = (typeof CAMPAIGN_DRAFT_GROUPS)[number];

export const CAMPAIGN_DRAFT_GROUP_LABELS: Readonly<Record<CampaignDraftGroup, string>> = {
	emails: 'Emails',
	call_script: 'Call scripts',
	voicemail_gatekeeper: 'Voicemail and gatekeeper lines',
	turnarounds: '"If they say" turnarounds',
	linkedin: 'LinkedIn messages',
};

export class CampaignDraftRequestDto {
	@IsArray()
	@ArrayMaxSize(10)
	@IsIn(CAMPAIGN_DRAFT_GROUPS as unknown as Array<string>, { each: true })
	public groups!: Array<CampaignDraftGroup>;

	/** `unedited`: only drafts nobody edited; `all`: everything, edits kept as an earlier version. */
	@IsIn(['unedited', 'all'])
	public mode!: 'unedited' | 'all';
}

export interface CampaignDraftResultDto {
	contents: Array<CampaignContentEntity>;
	/** Drafts rewritten. */
	written: number;
	/** Drafts left alone because someone edited them (mode `unedited`). */
	keptEdited: number;
	/** Why nothing was written by AI, when it wasn't (the plain editor still works). */
	error: string | null;
}

export class RestoreCampaignContentDto {
	@IsInt()
	@Min(1)
	public versionNo!: number;
}

/** A rep's own version of a script (call script, voicemail, gatekeeper). */
export class SaveMyCampaignScriptDto {
	@IsOptional()
	@IsInt()
	@Min(1)
	public stepNo?: number | null;

	@IsIn(['call_script', 'voicemail', 'gatekeeper'])
	public kind!: string;

	@IsString()
	@MaxLength(20000)
	public body!: string;
}

export type CampaignContentVersionDto = Pick<CampaignContentVersionEntity, 'versionNo' | 'subjectA' | 'subjectB' | 'body' | 'source' | 'createdDate'> & {
	createdByName: string | null;
};

/** One person to preview copy as: their merge facts. */
export interface CampaignPreviewPersonDto {
	memberId: number;
	name: string;
	accountName: string | null;
	facts: Record<string, string | null>;
}

export interface CampaignMergeCoverageDto {
	field: string;
	/** People in the campaign with a value for the field. */
	have: number;
	total: number;
}

export interface CampaignCopyPreviewDto {
	people: Array<CampaignPreviewPersonDto>;
	coverage: Array<CampaignMergeCoverageDto>;
	/** The tenant's person and account custom fields, offered as insert chips. */
	customFields: Array<{ field: string; label: string }>;
}

export class SaveCampaignWritingDefaultsDto {
	@IsString()
	@MaxLength(20000)
	public instructions!: string;
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

export interface CampaignStepReadinessDto {
	step: CampaignBuilderStep;
	ready: boolean;
	/** The second line on the step's tile ("382 people", "2 of 3 emails approved"). */
	summary: string;
	/** What is still missing, one line each. */
	missing: Array<string>;
}

/** One row of "For one person": when each step comes due if everything happens on time. */
export interface CampaignTimelineRowDto {
	stepNo: number;
	type: CampaignStepType | string;
	/** "Email A", "Call 1", "LinkedIn message". */
	label: string;
	/** Days from the first step (calendar days). */
	day: number;
	/** The date it would come due for someone starting on the timeline's start date (YYYY-MM-DD). */
	date: string;
}

export interface CampaignTimelineDto {
	start: string;
	rows: Array<CampaignTimelineRowDto>;
	/** Calendar days from the first step to the last. */
	totalDays: number;
}

/** Senders and timing: the estimate for the people in the campaign. */
export interface CampaignEstimateDto {
	/** The day the first person starts (YYYY-MM-DD). */
	start: string;
	/** The day the last person gets their first step. */
	lastStart: string;
	/** About when everyone has been through every step. */
	finish: string;
	/** Sending days needed to start everyone at the daily limit. */
	startDays: number;
	dailyNewPeople: number;
}

export interface CampaignSendersDto {
	connected: boolean;
	groups: Array<InstantlySenderGroupDto>;
	peopleSlots: InstantlyPeopleSlotsDto;
	/** The tenant's Instantly defaults, used where the campaign has none. */
	defaults: { sendDays: Array<number>; sendFrom: string; sendTo: string; dailyNewPeople: number; timezone: string };
	readError: string | null;
}

export interface CampaignBuilderDto {
	campaign: CampaignEntity;
	steps: Array<CampaignStepEntity>;
	/** The campaign's copy (owner null). */
	contents: Array<CampaignContentEntity>;
	/** The signed-in person's own scripts. */
	myContents: Array<CampaignContentEntity>;
	/** People who keep their own version of a script. */
	ownScriptPeople: Array<{ userId: number; name: string }>;
	readiness: Array<CampaignStepReadinessDto>;
	people: CampaignPeopleSummaryDto;
	peopleRules: CampaignPeopleRulesDto;
	timeline: CampaignTimelineDto;
	estimate: CampaignEstimateDto | null;
	instantlyConnected: boolean;
	/** Who saved last and when. */
	savedBy: string | null;
	savedAt: string | null;
	/**
	 * PD-17625 — the furthest step anyone has reached (0 before anyone has):
	 * steps up to it keep their place and type while the campaign runs.
	 */
	reachedThrough: number;
	/** People whose run finished without a reply (they can continue into steps added at the end). */
	finishedCount: number;
	/** Whether the campaign is in Instantly (its first email then stays first). */
	inInstantly: boolean;
}

export class StartCampaignDto {
	/** When the first people start; omitted = now. */
	@IsOptional()
	@IsDateString()
	public startAt?: string | null;
}

/** A row of the campaigns list (PD-17620 adds the working columns). */
export interface CampaignListRowDto {
	id: number;
	name: string;
	status: string;
	kind: string | null;
	steps: Array<CampaignStepType | string>;
	members: number;
	modifiedDate: string | null;
	startDate: string | null;
	ownerName: string | null;
}
